import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AlertCircle, ArrowRight, Check, Eye, EyeOff } from 'lucide-react'
import { register } from '../actions/userActions'
import { USER_REGISTER_RESET } from '../constants/userConstants'
import SEO from '../components/SEO'
import './RegisterScreen.css'

const MIN_PASSWORD = 8

// Published capability copy, carried over from the landing page. What an
// account is for belongs beside the form that creates one.
const CAPABILITIES = [
  {
    term: 'Report formats',
    desc: 'One narrative becomes a CHART, SOAP, or DCHART report. You pick the format when you start.',
  },
  {
    term: 'Template merge',
    desc: 'Your service’s template is merged in. Where the two conflict, what you wrote supersedes it.',
  },
  {
    term: 'Protocol questions',
    desc: 'Ask questions against your own protocol document and get the answer with the section it came from.',
  },
]

function RegisterScreen() {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [checkedBox, setCheckedBox] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [errors, setErrors] = useState({})
  const [done, setDone] = useState(false)

  const dispatch = useDispatch()
  const location = useLocation()
  const termsRef = useRef(null)

  const redirect = location.search ? location.search.split('=')[1] : '/'

  const userRegister = useSelector((state) => state.userRegister)
  const { error, loading, registered } = userRegister

  // Success is consumed once, then the redux flag is reset so re-entering the
  // page doesn't re-open the confirmation.
  useEffect(() => {
    if (!registered) return
    setDone(true)
    dispatch({ type: USER_REGISTER_RESET })
  }, [registered, dispatch])

  // Native <dialog> gives focus trapping, Esc-to-close and ::backdrop for free.
  useEffect(() => {
    const node = termsRef.current
    if (!node) return
    if (showTerms && !node.open) node.showModal()
    if (!showTerms && node.open) node.close()
  }, [showTerms])

  const clearError = (key) =>
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev))

  const submitHandler = (e) => {
    e.preventDefault()

    const next = {}
    if (password.length < MIN_PASSWORD) {
      next.password = `Use at least ${MIN_PASSWORD} characters.`
    }
    if (confirmPassword !== password) {
      next.confirm = 'This doesn’t match the password above.'
    }
    if (!checkedBox) {
      next.form = 'Please accept the terms and conditions to continue.'
    }

    setErrors(next)
    if (Object.keys(next).length > 0) return

    dispatch(register(firstName, lastName, email, password))
  }

  const serverError = typeof error === 'string' ? error : error ? 'We couldn’t create your account.' : null
  const formError = errors.form || serverError

  return (
    <>
      <SEO
        title="Register - PCR Assistant"
        description="Create your PCR Assistant account to access AI-powered PCR protocol generation, management tools, and more."
        canonicalUrl="/register"
      />

      <div className="rs">
        <div className="rs__wrap rs__grid">
          {/* ---- the task half ------------------------------------- */}
          <div className="rs__col">
            <header className="rs__head">
              <h1 className="rs__title">Create an account.</h1>
              <p className="rs__lede">
                Takes a minute. You&rsquo;ll be writing your first report straight after.
              </p>
            </header>

            {done ? (
              <div className="rs__done" role="status">
                <div className="rs__done-head">
                  <Check size={18} aria-hidden="true" />
                  <span className="rs__done-title">Account created</span>
                </div>
                <p>Sign in with the email and password you just chose.</p>
                <Link className="rs__btn rs__btn--primary" to="/login">
                  Sign in
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            ) : (
              <>
                {formError && (
                  <p className="rs__error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <span>{formError}</span>
                  </p>
                )}

                <form className="rs__form" onSubmit={submitHandler} noValidate>
                  <div className="rs__row">
                    <div className="rs__field">
                      <label className="rs__label" htmlFor="rs-first">
                        First name
                      </label>
                      <div className="rs__control">
                        <input
                          id="rs-first"
                          className="rs__input"
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          autoComplete="given-name"
                          required
                        />
                      </div>
                    </div>

                    <div className="rs__field">
                      <label className="rs__label" htmlFor="rs-last">
                        Last name
                      </label>
                      <div className="rs__control">
                        <input
                          id="rs-last"
                          className="rs__input"
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          autoComplete="family-name"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rs__field">
                    <label className="rs__label" htmlFor="rs-email">
                      Email address
                    </label>
                    <div className="rs__control">
                      <input
                        id="rs-email"
                        className="rs__input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="rs__field">
                    <label className="rs__label" htmlFor="rs-password">
                      Password
                    </label>
                    <div className="rs__control">
                      <input
                        id="rs-password"
                        className="rs__input rs__input--pw"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value)
                          clearError('password')
                        }}
                        autoComplete="new-password"
                        aria-describedby="rs-password-help"
                        aria-invalid={errors.password ? 'true' : undefined}
                        required
                      />
                      <button
                        type="button"
                        className="rs__pwtoggle"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        aria-pressed={showPassword}
                      >
                        {showPassword ? (
                          <EyeOff size={18} aria-hidden="true" />
                        ) : (
                          <Eye size={18} aria-hidden="true" />
                        )}
                      </button>
                    </div>
                    <p
                      className="rs__help"
                      id="rs-password-help"
                      data-state={errors.password ? 'error' : 'idle'}
                    >
                      {errors.password || `Minimum ${MIN_PASSWORD} characters.`}
                    </p>
                  </div>

                  <div className="rs__field">
                    <label className="rs__label" htmlFor="rs-confirm">
                      Confirm password
                    </label>
                    <div className="rs__control">
                      <input
                        id="rs-confirm"
                        className="rs__input rs__input--pw"
                        type={showConfirm ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value)
                          clearError('confirm')
                        }}
                        autoComplete="new-password"
                        aria-describedby="rs-confirm-help"
                        aria-invalid={errors.confirm ? 'true' : undefined}
                        required
                      />
                      <button
                        type="button"
                        className="rs__pwtoggle"
                        onClick={() => setShowConfirm((v) => !v)}
                        aria-label={showConfirm ? 'Hide password' : 'Show password'}
                        aria-pressed={showConfirm}
                      >
                        {showConfirm ? (
                          <EyeOff size={18} aria-hidden="true" />
                        ) : (
                          <Eye size={18} aria-hidden="true" />
                        )}
                      </button>
                    </div>
                    <p
                      className="rs__help"
                      id="rs-confirm-help"
                      data-state={errors.confirm ? 'error' : 'idle'}
                    >
                      {errors.confirm || ''}
                    </p>
                  </div>

                  <div className="rs__check">
                    <input
                      id="rs-terms"
                      type="checkbox"
                      checked={checkedBox}
                      onChange={(e) => {
                        setCheckedBox(e.target.checked)
                        clearError('form')
                      }}
                      aria-label="I have read the terms and conditions"
                    />
                    <span className="rs__check-text">
                      <label htmlFor="rs-terms">I have read the</label>{' '}
                      <button
                        type="button"
                        className="rs__linkbtn"
                        onClick={() => setShowTerms(true)}
                      >
                        terms and conditions
                      </button>
                    </span>
                  </div>

                  <button type="submit" className="rs__btn rs__btn--primary" disabled={loading}>
                    {loading ? 'Creating account…' : 'Create account'}
                    {!loading && <ArrowRight size={16} aria-hidden="true" />}
                  </button>
                </form>

                <p className="rs__meta">
                  Already have an account?{' '}
                  <Link
                    className="rs__link"
                    to={redirect ? `/login?redirect=${redirect}` : '/login'}
                  >
                    Sign in
                  </Link>
                </p>
              </>
            )}
          </div>

          {/* ---- the context half — the one graphite surface -------- */}
          <aside className="rs__panel" aria-labelledby="rs-panel-title">
            <span className="rs__panel-label">With an account</span>
            <h2 id="rs-panel-title">Write the narrative. Not the format.</h2>
            <ul className="rs__panel-list">
              {CAPABILITIES.map((c) => (
                <li className="rs__panel-item" key={c.term}>
                  <b>{c.term}</b>
                  <span>{c.desc}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>

        {/* ---- terms dialog -------------------------------------------
            WARNING — PLACEHOLDER CONTENT, NOT SHIPPABLE.
            The body below is the original placeholder string, preserved
            verbatim on purpose: writing the actual terms is a legal decision,
            not a design one. Until real terms are published here, the checkbox
            above collects consent to nothing. Replace before launch. */}
        <dialog
          ref={termsRef}
          className="rs__dialog"
          onClose={() => setShowTerms(false)}
          aria-labelledby="rs-terms-title"
        >
          <div className="rs__dialog-head">
            <h2 id="rs-terms-title">Terms and conditions</h2>
          </div>
          <div className="rs__dialog-body">
            <p>Terms and conditions content goes here...</p>
          </div>
          <div className="rs__dialog-foot">
            <button type="button" className="rs__btn" onClick={() => setShowTerms(false)}>
              Close
            </button>
          </div>
        </dialog>
      </div>
    </>
  )
}

export default RegisterScreen
